import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Gauge, LayoutDashboard, MapPin, Radio, Smartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { isCgmPilotExperience } from '../config/cgmPilot';

interface BottomNavItem {
  href: string;
  nameKey: string;
  icon: React.ElementType;
}

const STANDARD_ITEMS: BottomNavItem[] = [
  { href: '/dashboard', nameKey: 'nav:dashboard', icon: LayoutDashboard },
  { href: '/iot', nameKey: 'nav:iot', icon: Radio },
  { href: '/devices', nameKey: 'nav:devices', icon: Smartphone },
];

const CGM_ITEMS: BottomNavItem[] = [
  { href: '/dashboard', nameKey: 'agrisafeR1:nav.dashboard', icon: LayoutDashboard },
  { href: '/sectors', nameKey: 'agrisafeR1:nav.sectors', icon: MapPin },
  { href: '/pilotage', nameKey: 'agrisafeR1:nav.pilotageShort', icon: Radio },
  { href: '/pumping', nameKey: 'agrisafeR1:nav.pumpingShort', icon: Gauge },
  { href: '/devices', nameKey: 'agrisafeR1:nav.equipment', icon: Smartphone },
];

const BottomNavigation: React.FC = () => {
  const { t } = useTranslation(['nav', 'agrisafeR1']);
  const { user } = useAuth();
  const location = useLocation();
  const items = isCgmPilotExperience(user?.org_id, user?.role) ? CGM_ITEMS : STANDARD_ITEMS;

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-30 bg-[var(--surface)] border-t border-[var(--border-light)] lg:hidden"
      data-testid="bottom-navigation"
      style={{
        // R6: SOVERDI adds 10px on top of the inset so the labels are not
        // flush against the home indicator on gesture-navigation devices.
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 10px)',
      }}
    >
      <div className="flex items-stretch justify-around min-h-[64px]">
        {items.map((item) => {
          const active = location.pathname === item.href;
          const Icon = item.icon;
          return (
            <Link
              key={item.href}
              to={item.href}
              className={`
                flex flex-col items-center justify-center flex-1 min-h-[48px] min-w-[48px]
                py-2 px-0.5 transition-colors duration-200 active:bg-[var(--gray-100)]
                ${active ? 'text-[var(--color-primary)]' : 'text-[var(--text-tertiary)]'}
              `}
              data-testid={`bottom-nav-${item.href}`}
              {...(active ? ({ 'aria-current': 'page' } as const) : {})}
            >
              <div className={`p-1.5 rounded-lg transition-colors duration-200 ${active ? 'bg-[var(--color-primary)]/10' : ''}`}>
                <Icon className="w-5 h-5" strokeWidth={active ? 2.5 : 2} />
              </div>
              {/* R6: R4 truncated these labels, which cut "Équipements" at 375px
                  and hid the destination entirely in longer locales. Following
                  the SOVERDI bar, the label now wraps instead of being clipped:
                  the row is centred and the bar grows by one text line at worst,
                  so no destination is ever unreadable. */}
              <span
                className={`text-[10px] mt-0.5 leading-tight text-center break-words ${active ? 'font-semibold' : 'font-medium'}`}
              >
                {t(item.nameKey)}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNavigation;
